import { useEffect, useState } from 'react';
import { message } from 'antd';
import {
  useGetUserSettingsQuery,
  useSaveUserSettingsMutation,
} from '../../services/UserSettings';
import { isValidNumberInput } from '../../utils/formateNumbers';

const DEFAULT_AGENT_COSTS = 3.75;
const DEFAULT_NOTARY_COSTS = 1.0;
const DEFAULT_REPAYMENT   = 2.0;

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(0,0,0,0.40)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    width: 560,
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#FFFFFF',
    borderRadius: 24,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
    padding: 32,
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: '32px',
    color: '#1F1F1F',
    margin: 0,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: '#F3F3F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    color: '#777888',
    cursor: 'pointer',
    transition: 'background 150ms',
    flexShrink: 0,
  },
  divider: {
    height: 1,
    background: '#E0E0DF',
    marginBottom: 24,
  },
  sectionHeadline: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#8C8FA0',
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: '#5D5D6D',
    marginBottom: 6,
  },
  input: {
    height: 44,
    width: '100%',
    border: '1px solid #C8C8D0',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 16,
    lineHeight: '24px',
    color: '#1F1F1F',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#FFFFFF',
    transition: 'border-color 150ms, box-shadow 150ms',
  },
  inputFocus: {
    borderColor: '#1DB3B3',
    boxShadow: '0 0 0 3px rgba(28,139,130,0.12)',
  },
  inputSuffix: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  suffixText: {
    position: 'absolute',
    right: 12,
    fontSize: 16,
    color: '#8C8FA0',
    pointerEvents: 'none',
  },
  totalBadge: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
    fontSize: 14,
    lineHeight: '20px',
    color: '#838799',
  },
  saveBtn: {
    width: '100%',
    height: 48,
    background: '#1DB3B3',
    border: 'none',
    borderRadius: 8,
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 150ms',
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  cancelBtn: {
    width: '100%',
    height: 44,
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    color: '#777888',
    fontSize: 16,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 150ms',
    marginTop: 8,
  },
};

// ─── EK-Toggle ───────────────────────────────────────────────────────────────
function EkToggle({ mode, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      background: '#E0E0DF',
      borderRadius: 8,
      padding: 3,
    }}>
      {['eur', 'pct'].map(m => (
        <button
          key={m}
          onClick={() => onChange(m)}
          style={{
            width: 48,
            height: 28,
            borderRadius: 6,
            border: mode === m ? '1px solid rgba(0,0,0,0.06)' : '1px solid transparent',
            background: mode === m ? '#FFFFFF' : 'transparent',
            color: mode === m ? '#1F1F1F' : '#838799',
            fontWeight: mode === m ? 600 : 500,
            fontSize: 14,
            lineHeight: '20px',
            cursor: 'pointer',
            transition: 'background 150ms',
            boxShadow: mode === m ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
          }}
        >
          {m === 'eur' ? 'EUR' : '%'}
        </button>
      ))}
    </div>
  );
}

// ─── InputWithSuffix ─────────────────────────────────────────────────────────
function InputWithSuffix({ value, onChange, placeholder, suffix, style: extraStyle }) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={styles.inputSuffix}>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...styles.input,
          paddingRight: suffix ? 36 : 12,
          ...(focused ? styles.inputFocus : {}),
          ...(extraStyle || {}),
        }}
      />
      {suffix && (
        <span style={styles.suffixText}>{suffix}</span>
      )}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
const SettingsModal = ({ open, onCancel, userID, userName, userEmail }) => {
  const [equityMode, setEquityMode]         = useState('eur');
  const [equityAbsolute, setEquityAbsolute] = useState('');
  const [equityRatio, setEquityRatio]       = useState('');
  const [interestRate, setInterestRate]     = useState('');
  const [repayment, setRepayment]           = useState('');
  const [agentCosts, setAgentCosts]         = useState('');
  const [notaryCosts, setNotaryCosts]       = useState('');

  const { data: settingsData, isFetching } = useGetUserSettingsQuery(userID, {
    skip: !open || !userID,
  });

  const [saveSettings, { isLoading: isSaving }] = useSaveUserSettingsMutation();

  // Initialwerte aus API laden
  useEffect(() => {
    if (settingsData?.success) {
      const s = settingsData.settings;
      const mode = s.EquityMode || 'eur';
      setEquityMode(mode);
      setInterestRate((s.InterestRate * 100).toFixed(2));
      setRepayment(s.Repayment != null ? (s.Repayment * 100).toFixed(2) : String(DEFAULT_REPAYMENT));
      if (mode === 'eur') {
        setEquityAbsolute(s.EquityAbsolute > 0 ? String(s.EquityAbsolute) : '80000');
      } else {
        setEquityRatio((s.EquityRatio * 100).toFixed(2));
      }
      setAgentCosts(((s.AgentCosts ?? DEFAULT_AGENT_COSTS / 100) * 100).toFixed(2));
      setNotaryCosts(((s.NotaryCosts ?? DEFAULT_NOTARY_COSTS / 100) * 100).toFixed(2));
    }
  }, [settingsData]);

  // Berechnete Werte
  const agentNum   = isNaN(parseFloat(agentCosts))   ? DEFAULT_AGENT_COSTS : parseFloat(agentCosts);
  const notaryNum  = isNaN(parseFloat(notaryCosts))  ? DEFAULT_NOTARY_COSTS : parseFloat(notaryCosts);
  const totalNebenkosten = (agentNum + notaryNum).toFixed(1);

  // Handler
  const makeNumberHandler = (setter, min, max) => (e) => {
    const v = e.target.value;
    if (v === '' || isValidNumberInput(v)) {
      const n = parseFloat(v);
      if (v === '' || (n >= min && n <= max)) setter(v);
    }
  };

  const handleEquityAbsoluteChange = (e) => {
    const v = e.target.value;
    if (v === '' || /^\d+$/.test(v)) setEquityAbsolute(v);
  };

  const handleSave = async () => {
    const parsedInterest = parseFloat(interestRate);
    if (isNaN(parsedInterest) || parsedInterest < 0 || parsedInterest > 100) {
      message.error('Bitte einen gültigen Zinssatz eingeben (0–100).');
      return;
    }

    const parsedRepayment = parseFloat(repayment);
    if (isNaN(parsedRepayment) || parsedRepayment < 0 || parsedRepayment > 100) {
      message.error('Bitte eine gültige Tilgung eingeben (0–100).');
      return;
    }

    let equityRatioForApi  = 0;
    let equityAbsoluteForApi = 0;

    if (equityMode === 'eur') {
      const parsed = parseFloat(equityAbsolute);
      if (isNaN(parsed) || parsed <= 0) {
        message.error('Bitte einen gültigen Eigenkapital-Betrag in € eingeben.');
        return;
      }
      equityAbsoluteForApi = parsed;
    } else {
      const parsed = parseFloat(equityRatio);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        message.error('Bitte einen gültigen Eigenkapital-Anteil eingeben (0–100).');
        return;
      }
      equityRatioForApi = parsed / 100;
    }

    const payload = {
      UserID: userID,
      Username: userName,
      Email: userEmail,
      EquityMode: equityMode,
      EquityRatio: equityRatioForApi,
      EquityAbsolute: equityAbsoluteForApi,
      InterestRate: parsedInterest / 100,
      Repayment: parsedRepayment / 100,
      AgentCosts: agentNum / 100,
      NotaryCosts: notaryNum / 100,
    };

    try {
      const result = await saveSettings(payload).unwrap();
      if (result.success) {
        message.success('Einstellungen gespeichert!');
        onCancel();
        // Kein window.location.reload() — RTK Query invalidiert Tags automatisch via invalidatesTags: ['UserSettings']
      } else {
        message.error('Fehler beim Speichern.');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Ein Fehler ist aufgetreten.');
    }
  };

  if (!open) return null;

  const isLoading = isSaving || isFetching;

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Einstellungen</h2>
          <button
            style={styles.closeBtn}
            onClick={onCancel}
            onMouseEnter={e => { e.currentTarget.style.background = '#E0E0DF'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#F3F3F5'; }}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        <div style={styles.divider} />

        {/* ── FINANZIERUNG ── */}
        <div style={styles.sectionHeadline}>Finanzierung</div>

        {/* Eigenkapital */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={styles.label}>Eigenkapital</span>
            <EkToggle mode={equityMode} onChange={(m) => { setEquityMode(m); setEquityAbsolute(''); setEquityRatio(''); }} />
          </div>
          {equityMode === 'eur' ? (
            <InputWithSuffix
              value={equityAbsolute}
              onChange={handleEquityAbsoluteChange}
              placeholder="z.B. 80000"
              suffix="€"
            />
          ) : (
            <InputWithSuffix
              value={equityRatio}
              onChange={makeNumberHandler(setEquityRatio, 0, 100)}
              placeholder="z.B. 20"
              suffix="%"
            />
          )}
        </div>

        {/* Finanzierungszins */}
        <div style={{ marginBottom: 16 }}>
          <div style={styles.label}>Finanzierungszins</div>
          <InputWithSuffix
            value={interestRate}
            onChange={makeNumberHandler(setInterestRate, 0, 100)}
            placeholder="z.B. 4"
            suffix="%"
          />
        </div>

        {/* Tilgung */}
        <div style={{ marginBottom: 24 }}>
          <div style={styles.label}>Tilgung</div>
          <InputWithSuffix
            value={repayment}
            onChange={makeNumberHandler(setRepayment, 0, 100)}
            placeholder="z.B. 2"
            suffix="%"
          />
        </div>

        {/* Trennlinie vor NEBENKOSTEN */}
        <div style={{ ...styles.divider, marginBottom: 0 }} />
        <div style={{ ...styles.sectionHeadline, marginTop: 24 }}>Nebenkosten</div>

        {/* Grunderwerbsteuer-Hinweis */}
        <div style={{ fontSize: 13, color: '#8C8FA0', marginTop: 8, fontStyle: 'italic', marginBottom: 16 }}>
          Grunderwerbsteuer wird automatisch aus dem Bundesland des Objekts ermittelt.
        </div>

        {/* Makler + Notar nebeneinander */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={styles.label}>Maklerprovision</div>
            <InputWithSuffix
              value={agentCosts}
              onChange={makeNumberHandler(setAgentCosts, 0, 10)}
              placeholder="3,5"
              suffix="%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.label}>Notarkosten</div>
            <InputWithSuffix
              value={notaryCosts}
              onChange={makeNumberHandler(setNotaryCosts, 0, 5)}
              placeholder="2,0"
              suffix="%"
            />
          </div>
        </div>

        {/* Nebenkosten gesamt */}
        <div style={styles.totalBadge}>
          <span>Nebenkosten gesamt:&nbsp;</span>
          <span style={{ fontWeight: 600, color: '#1F1F1F' }}>{totalNebenkosten.replace('.', ',')} %</span>
        </div>

        {/* ── Footer ── */}
        <div style={{ ...styles.divider, marginTop: 24 }} />

        <button
          onClick={handleSave}
          disabled={isLoading}
          style={{
            ...styles.saveBtn,
            background: isLoading ? '#A8DEDE' : '#1DB3B3',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!isLoading) e.currentTarget.style.background = '#0E7A6B'; }}
          onMouseLeave={e => { if (!isLoading) e.currentTarget.style.background = '#1DB3B3'; }}
        >
          {isLoading && (
            <div style={{
              width: 16, height: 16,
              border: '2px solid rgba(255,255,255,0.4)',
              borderTopColor: '#FFFFFF',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
          )}
          {isLoading ? 'Wird gespeichert…' : 'Speichern'}
        </button>

        <button
          onClick={onCancel}
          style={styles.cancelBtn}
          onMouseEnter={e => { e.currentTarget.style.background = '#F3F3F5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          Abbrechen
        </button>
      </div>

      {/* Spinner-Keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SettingsModal;
